import Box, { BoxProps } from '@material-ui/core/Box';
import IconButton from '@material-ui/core/IconButton';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import deleteIcon from 'assets/icon/delete.svg';
import editIcon from 'assets/icon/edit.svg';
import PrintIcon from 'assets/icon/printer.svg';
import Button from 'components/Button';
import CustomDropdown from 'components/CustomDropdown';
import SearchBar from 'components/SearchBar';
import { SearchParams } from 'components/SearchBar/types';
import VersionHistory, { ItemProps } from 'components/VersionHistory';
import React, { ReactNode, useEffect, useState } from 'react';
import SVG from 'react-inlinesvg';
import styled from 'styled-components';

type SectionWrapperProps = {
  title?: string;
  subTitle?: string;
  children?: ReactNode;
  className?: string;
  wrapperProps?: BoxProps;
  isShowBaseButton?: boolean;
  textButton?: string;
  textButtonExtra?: string;
  minimizable?: boolean;
  /**
   * Turns the collapse into a controlled component. When set, the parent owns the
   * expanded state (needed where the state decides what gets printed).
   */
  minimized?: boolean;
  onMinimizedChange?: (minimized: boolean) => void;
  handleClick?: () => void;
  handleClickExtraBtn?: () => void;
  handleDelete?: () => void;
  handleEdit?: () => void;
  handleOnFilterChange?: (filters: SearchParams) => void;
  handlePrint?: () => void;
  onMenuItemClick?: (menuItem: string | null | undefined) => void;
  isActiveBtn?: boolean;
  isShowDropdownButton?: boolean;
  isShowDeleteButton?: boolean;
  isShowEditButton?: boolean;
  isShowVersionHistory?: boolean;
  versionHistoryId?: string;
  versionHistoryData?: ItemProps;
  dropDownList?: { value: string; label: string }[];
  dropdownLabel?: string;
  isActiveExtraBtn?: boolean;
  isShowExtraButton?: boolean;
  isShowFilterButton?: boolean;
  isShowPrintButton?: boolean;
  isNarrowMargin?: boolean;
  enableFreeTextSearchForFilter?: boolean;
  enableSingleDateSearchForFilter?: boolean;
  enableFilterButton?: boolean;
  filterButtonCheckboxItems?: { value: string; label: string }[];
  noSubmitOnClick?: boolean;
  positionRelative?: boolean;
  defaultMinimized?: boolean;
  noMarginTopBottom?: boolean;
  customButtons?: ReactNode;
  titleAddon?: ReactNode;
};

const Wrapper = styled.div<{ $relative: boolean; $noMarginTopBottom: boolean }>`
  background: ${(props) => props.theme.colors.white};
  border-radius: 8px;
  padding: ${(props) => (props.$noMarginTopBottom ? '5px' : '16px')};
  margin-bottom: ${(props) => (props.$noMarginTopBottom ? '0px' : '15px')};
  margin-top: ${(props) => (props.$noMarginTopBottom ? '0px' : '20px')};
  line-height: 21px;
  position: ${(props) => (props.$relative ? 'relative' : 'initial')};
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const Title = styled.span`
  font-weight: bold;
  font-size: 18px;
  line-height: 27px;
  color: ${(props) => props.theme.colors.nero};
  margin-right: 10px;
`;

export const SubTitle = styled.span`
  font-size: 18px;
  line-height: 27px;
  font-weight: 500;
  color: ${(props) => props.theme.colors.nero};
`;

const BaseButton = styled.button`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px;
  background: #5cabdd;
  border-radius: 8px;
  flex: none;
  order: 0;
  flex-grow: 0;
  border: none;
  color: #ffffff;
  font-weight: 600;
  font-size: 12px;
  line-height: 18px;
  height: 40px;

  :disabled {
    opacity: 1;
    background-color: #757677 !important;
  }
`;

const SectionWrapper = ({
  title,
  subTitle,
  children,
  className,
  wrapperProps,
  isShowBaseButton,
  textButton,
  textButtonExtra,
  minimizable,
  minimized: controlledMinimized,
  onMinimizedChange,
  handleClick,
  handleClickExtraBtn,
  handleOnFilterChange,
  handlePrint,
  onMenuItemClick,
  handleDelete,
  handleEdit,
  isActiveBtn,
  isActiveExtraBtn,
  isShowDropdownButton = false,
  isShowDeleteButton = false,
  isShowEditButton = false,
  isShowVersionHistory = false,
  isShowPrintButton = false,
  isNarrowMargin = false,
  versionHistoryId,
  versionHistoryData,
  dropDownList,
  dropdownLabel,
  isShowExtraButton = false,
  isShowFilterButton = false,
  enableFreeTextSearchForFilter = false,
  enableSingleDateSearchForFilter = false,
  enableFilterButton = false,
  filterButtonCheckboxItems,
  noSubmitOnClick = false,
  positionRelative = false,
  defaultMinimized = false,
  noMarginTopBottom = false,
  customButtons,
  titleAddon,
}: SectionWrapperProps) => {
  const btnId = title
    ? `${title?.toLocaleLowerCase().replace(' ', '-')}-button`
    : `${subTitle?.toLocaleLowerCase().replace(' ', '-')}-button`;

  const extraBtnId = title
    ? `${title?.toLocaleLowerCase().replace(' ', '-')}-extraButton`
    : `${subTitle?.toLocaleLowerCase().replace(' ', '-')}-extraButton`;

  const [filters, setFilters] = useState<SearchParams>({});
  const [selectedMenuItem, setSelectedMenuItem] = useState<string | null>();
  const [internalMinimized, setInternalMinimized] = useState<boolean>(false);
  const isControlled = controlledMinimized !== undefined;
  const minimized = isControlled ? controlledMinimized : internalMinimized;

  const toggleMinimized = (): void => {
    if (isControlled) {
      onMinimizedChange?.(!minimized);
      return;
    }
    setInternalMinimized(!minimized);
  };

  useEffect(() => {
    if (handleOnFilterChange) {
      handleOnFilterChange(filters);
    }
  }, [filters]);

  useEffect(() => {
    if (onMenuItemClick) {
      onMenuItemClick(selectedMenuItem);
    }
    setSelectedMenuItem(null);
  }, [selectedMenuItem]);

  useEffect(() => {
    setInternalMinimized(defaultMinimized);
  }, [defaultMinimized]);

  const handleBaseButtonDisabled = () => {
    if (defaultMinimized && minimized) {
      return true;
    }
    return isActiveBtn;
  };

  const handleExtraButtonDisabled = () => {
    if (defaultMinimized && minimized) {
      return true;
    }
    return isActiveExtraBtn;
  };

  return (
    <Wrapper
      className={className || ''}
      $relative={positionRelative}
      $noMarginTopBottom={noMarginTopBottom}
    >
      {title || subTitle || isShowBaseButton ? (
        <Box
          sx={{
            pb: minimized ? 0 : 2.25,
            display:
              isShowBaseButton ||
              isShowDropdownButton ||
              isShowDeleteButton ||
              customButtons ||
              isShowExtraButton
                ? 'flex'
                : 'block',
            justifyContent: 'space-between',
            ...(titleAddon && { flexWrap: 'wrap', gap: 1 }),
            '@media(max-width: 340px)': {
              display: 'block',
            },
          }}
          {...wrapperProps}
        >
          <Box
            sx={{
              display: 'flex',
              ...(titleAddon && {
                flex: '1 1 0%',
                flexWrap: 'wrap',
                alignItems: 'center',
                rowGap: 1,
              }),
            }}
          >
            <Box justifyContent={'center'}>
              {minimizable && (
                <IconButton
                  color="inherit"
                  size="medium"
                  aria-expanded={!minimized}
                  aria-label={title ?? subTitle}
                  onClick={toggleMinimized}
                  sx={{ mt: -1, ml: -1, p: 0.75 }}
                >
                  {minimized ? <ArrowRightIcon /> : <ArrowDropDownIcon />}
                </IconButton>
              )}
              {title && <Title>{title}</Title>}
            </Box>
            {titleAddon}
            {subTitle && <SubTitle>{subTitle}</SubTitle>}
            {isShowVersionHistory && versionHistoryId && versionHistoryData && (
              <VersionHistory
                id={versionHistoryId}
                items={[versionHistoryData]}
                value={versionHistoryId}
                withoutVersionSwitch={true}
              />
            )}
          </Box>
          {(isShowBaseButton ||
            isShowExtraButton ||
            isShowFilterButton ||
            isShowDropdownButton ||
            isShowDeleteButton ||
            isShowEditButton) && (
            <ButtonWrapper>
              {isShowBaseButton && (
                <BaseButton
                  className="hide-in-print"
                  id={btnId}
                  onClick={handleClick}
                  disabled={handleBaseButtonDisabled()}
                  type={noSubmitOnClick ? 'button' : undefined}
                >
                  {textButton}
                </BaseButton>
              )}
              {isShowExtraButton && (
                <BaseButton
                  className="hide-in-print"
                  id={extraBtnId}
                  onClick={handleClickExtraBtn}
                  disabled={handleExtraButtonDisabled()}
                >
                  {textButtonExtra}
                </BaseButton>
              )}
              {isShowFilterButton && (
                <SearchBar
                  onFilterChange={setFilters}
                  enableFreeText={enableFreeTextSearchForFilter}
                  enableSingleDateFilter={enableSingleDateSearchForFilter}
                  enableFilterButton={enableFilterButton}
                  items={filterButtonCheckboxItems}
                />
              )}
              {isShowPrintButton && (
                <Button className="btn-print" $isIcon={true} padding={'0'} onClick={handlePrint}>
                  <span className="svg-icon-md">
                    <SVG src={PrintIcon} />
                  </span>
                </Button>
              )}
              {isShowDropdownButton && (
                <CustomDropdown
                  onSelect={setSelectedMenuItem}
                  options={dropDownList}
                  buttonText={dropdownLabel}
                />
              )}
              {isShowEditButton && (
                <Button name="editButton" $isIcon={true} onClick={handleEdit}>
                  <SVG src={editIcon} style={{ width: '20px', height: '20px' }} />
                </Button>
              )}
              {isShowDeleteButton && (
                <Button name="deleteButton" $isIcon={true} $isAlert={true} onClick={handleDelete}>
                  <SVG src={deleteIcon} />
                </Button>
              )}
            </ButtonWrapper>
          )}
          {customButtons ?? null}
        </Box>
      ) : null}
      {!minimized && children}
    </Wrapper>
  );
};

export default SectionWrapper;
